"use client";

import React, { memo, useState, useCallback, useRef, useEffect } from "react";
import { useAuthStore, ROLE_LABELS } from "@/stores/auth-store";
import { logoutCurrentSession, clearSessionRolePreview } from "@/services/auth.service";
import SessionsPanel from "@/components/hrm/auth/SessionsPanel";
import UserProfileAvatar from "@/components/hrm/common/UserProfileAvatar";
import RolePreviewSwitcher from "@/components/hrm/layout/RolePreviewSwitcher";
import { pageToPath } from "@/lib/hrm-routing";
import { useDashboardConfig } from "@/hooks/use-dashboard-config";
import { usePermissions } from "@/hooks/use-permissions";

interface HRMHeaderProps {
  onToggleSidebar: () => void;
  onToggleDesktopCollapse: () => void;
  isSidebarCollapsed: boolean;
  onNavigate: (page: string) => void;
  currentPage: string;
}

const HRMHeader = memo(function HRMHeader({
  onToggleSidebar,
  onToggleDesktopCollapse,
  isSidebarCollapsed,
  onNavigate,
  currentPage,
}: HRMHeaderProps) {
  const displayUser = useAuthStore((s) => s.displayUser);
  const logout = useAuthStore((s) => s.logout);
  const clearRolePreview = useAuthStore((s) => s.clearRolePreview);
  const syncPermissions = useAuthStore((s) => s.syncPermissions);
  const rolePreview = useAuthStore((s) => s.rolePreview);
  const { config } = useDashboardConfig();
  const { rolePreviewLabel, isRolePreview } = usePermissions();
  const [showNotif, setShowNotif] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [exitingPreview, setExitingPreview] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const userRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node))
        setShowNotif(false);
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleExitPreview = useCallback(async () => {
    setExitingPreview(true);
    try {
      const products = await clearSessionRolePreview();
      syncPermissions(products);
      clearRolePreview();
    } catch {
      clearRolePreview();
    } finally {
      setExitingPreview(false);
    }
  }, [clearRolePreview, syncPermissions]);

  const handleSignOut = useCallback(async () => {
    setIsSigningOut(true);
    try {
      await logoutCurrentSession();
    } finally {
      clearRolePreview();
      logout();
      setShowUserMenu(false);
      setIsSigningOut(false);
      window.history.replaceState(null, "", pageToPath("dashboard"));
    }
  }, [logout, clearRolePreview]);

  if (!displayUser) return null;

  const roleLabel =
    rolePreviewLabel ??
    config?.variantLabel ??
    displayUser.variantLabel ??
    ROLE_LABELS[displayUser.role];

  return (
    <>
      {isRolePreview && rolePreview && (
        <div className="fixed top-0 left-0 right-0 z-[35] h-9 flex items-center justify-center gap-3 px-3 bg-amber-500 text-amber-950 text-xs font-semibold shadow-sm">
          <i className="ri-eye-line" />
          <span>
            Viewing as <strong>{rolePreviewLabel}</strong> — UI and API use this role&apos;s exact permissions
          </span>
          <button
            type="button"
            disabled={exitingPreview}
            onClick={() => void handleExitPreview()}
            className="ml-2 px-2.5 py-0.5 rounded-md bg-amber-950/15 hover:bg-amber-950/25 font-bold disabled:opacity-60"
          >
            {exitingPreview ? "Exiting…" : "Exit preview"}
          </button>
        </div>
      )}
    <header
      className={`
        fixed right-[72px] h-16 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md
        border-b border-slate-200 dark:border-slate-800 z-30 flex min-w-0 items-center
        justify-between gap-2 sm:gap-3 lg:gap-4 px-3 sm:px-4 lg:px-6
              left-0 transition-[left,top] duration-300 ease-in-out
        ${isRolePreview ? "top-9" : "top-0"}
        ${isSidebarCollapsed ? "lg:left-[72px]" : "lg:left-[260px]"}
      `}
    >
      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
        {/* Mobile: open drawer */}
        <button
          type="button"
          onClick={onToggleSidebar}
          className="lg:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          aria-label="Open menu"
        >
          <i className="ri-menu-line text-xl text-slate-600 dark:text-slate-400" />
        </button>
        {/* Desktop: collapse to icon rail */}
        <button
          type="button"
          onClick={onToggleDesktopCollapse}
          className="hidden lg:flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          aria-label={
            isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
          }
        >
          <i
            className={`text-xl text-slate-600 dark:text-slate-400 transition-transform ${
              isSidebarCollapsed ? "ri-menu-unfold-line" : "ri-menu-fold-line"
            }`}
          />
        </button>
      </div>

      <div className="hidden sm:flex flex-1 min-w-0 max-w-md mx-2">
        <div className="relative w-full min-w-0">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
          <input
            type="text"
            placeholder="Search employees, actions..."
            className="w-full pl-9 pr-4 py-2 bg-slate-100 dark:bg-slate-800 border-0 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--hrm-primary)]/50"
          />
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center justify-end gap-2 sm:gap-3 flex-shrink-0">
        {/* Dashboard Switch Toggle */}
        {(currentPage === "dashboard" || currentPage === "dashboard-hr") && (
          <button
            type="button"
            onClick={() =>
              onNavigate(
                currentPage === "dashboard" ? "dashboard-hr" : "dashboard"
              )
            }
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-800 hover:border-[var(--hrm-primary)] dark:hover:border-[var(--hrm-primary)] text-slate-700 dark:text-slate-300 hover:text-[var(--hrm-primary-dark)] dark:hover:text-[var(--hrm-primary)] text-xs font-semibold transition-all shadow-sm bg-white dark:bg-slate-900"
            title={
              currentPage === "dashboard"
                ? "Switch to HR Dashboard"
                : "Switch to Standard Dashboard"
            }
          >
            <i
              className={
                currentPage === "dashboard"
                  ? "ri-pulse-line text-sm text-[var(--hrm-primary)] dark:text-[var(--hrm-primary)]"
                  : "ri-dashboard-line text-sm text-slate-500 dark:text-slate-400"
              }
            />
            <span>
              {currentPage === "dashboard" ? "HR Dashboard" : "Standard"}
            </span>
          </button>
        )}
        {/* Dark Mode Toggle */}
        <button
          onClick={() => document.documentElement.classList.toggle("dark")}
          className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          title="Toggle Dark Mode"
        >
          <i className="ri-sun-line text-lg text-amber-500 dark:hidden" />
          <i className="ri-moon-line text-lg text-slate-400 hidden dark:block" />
        </button>

        {/* Notifications */}
        <div ref={notifRef} className="relative">
          <button
            onClick={() => setShowNotif(!showNotif)}
            className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors relative"
          >
            <i className="ri-notification-3-line text-lg text-slate-600 dark:text-slate-400" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-[var(--hrm-primary)] rounded-full" />
          </button>

          {showNotif && (
            <>
              {/* Mobile Backdrop */}
              <div
                className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] sm:hidden"
                onClick={() => setShowNotif(false)}
              />

              {/* Notification Panel */}
              <div
                className="
          fixed sm:absolute
          top-16 sm:top-full
          left-1/2 sm:left-auto
          -translate-x-1/2 sm:translate-x-0
          right-auto sm:right-0
          mt-0 sm:mt-2
          
          w-[92vw] sm:w-80
          max-w-md
          
          bg-white dark:bg-slate-900
          border border-slate-200 dark:border-slate-800
          rounded-2xl sm:rounded-xl
          shadow-2xl
          
          z-50
          hrm-modal-enter
          overflow-hidden
        "
              >
                {/* Header */}
                <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">
                      Notifications
                    </span>

                    <span className="whitespace-nowrap text-xs font-medium px-2 py-0.5 rounded-full bg-[var(--hrm-primary-light)] dark:bg-[var(--hrm-primary)]/10 text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)]">
                      3 new
                    </span>
                  </div>
                </div>

                {/* Notification List */}
                <div className="py-1 max-h-[60vh] overflow-y-auto">
                  {[
                    {
                      icon: "ri-user-add-line",
                      color:
                        "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
                      text: "New candidate applied for Senior Developer",
                      time: "12 min ago",
                    },
                    {
                      icon: "ri-leaf-line",
                      color:
                        "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400",
                      text: "John Doe requested 3 days leave",
                      time: "5 min ago",
                    },
                    {
                      icon: "ri-money-dollar-circle-line",
                      color:
                        "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
                      text: "Payroll for March processed successfully",
                      time: "1 hr ago",
                    },
                  ].map((n, i) => (
                    <div
                      key={i}
                      className="
                flex items-start gap-3
                px-4 py-3
                hover:bg-slate-50 dark:hover:bg-slate-800/50
                transition-colors
                cursor-pointer
              "
                    >
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${n.color}`}
                      >
                        <i className={`${n.icon} text-sm`} />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-white break-words leading-relaxed">
                          {n.text}
                        </p>

                        <p className="text-[11px] mt-1 text-slate-400">
                          {n.time}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-800">
                  <button
                    onClick={() => {
                      setShowNotif(false);
                      onNavigate("notifications");
                    }}
                    className="
              w-full sm:w-auto
              text-center
              text-xs sm:text-sm
              font-semibold
              text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)]
              hover:text-[var(--hrm-primary-dark)]
              transition-colors
            "
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Divider */}
        <div className="w-px h-6 mx-1 bg-slate-200 dark:bg-slate-800" />

        {/* User Menu */}
        <div ref={userRef} className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2.5 py-1 pl-1 pr-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <UserProfileAvatar
              name={displayUser.name}
              profileImageUrl={displayUser.profileImageUrl}
              ringClassName="hrm-avatar-ring"
            />
            <div className="hidden sm:block text-left">
              <p className="text-xs font-semibold leading-tight text-slate-900 dark:text-white">
                {displayUser.name}
              </p>
              <p className="text-[10px] leading-tight text-slate-400">
                {roleLabel}
              </p>
            </div>
            <i className="ri-arrow-down-s-line text-sm text-slate-400" />
          </button>

          {showUserMenu && (
            <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl shadow-xl z-50 hrm-modal-enter">
              <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3">
                <UserProfileAvatar
                  name={displayUser.name}
                  profileImageUrl={displayUser.profileImageUrl}
                  size="md"
                />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                    {displayUser.name}
                  </p>
                  <p className="text-xs text-slate-400 truncate">{displayUser.email}</p>
                  <p className="text-[10px] mt-0.5 font-semibold text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)] truncate">
                    {roleLabel}
                    {isRolePreview ? " (preview)" : ""}
                  </p>
                </div>
              </div>
              <div className="py-1">
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    onNavigate("employee-profile");
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 transition-colors text-left"
                >
                  <i className="ri-user-line" /> My Profile
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    onNavigate("settings");
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 transition-colors text-left"
                >
                  <i className="ri-settings-3-line" /> Settings
                </button>
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    setShowSessions(true);
                  }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-600 dark:text-slate-400 transition-colors text-left"
                >
                  <i className="ri-device-line" /> Active sessions
                </button>
              </div>
              <RolePreviewSwitcher onPicked={() => setShowUserMenu(false)} />
              <div className="py-1 border-t border-slate-200 dark:border-slate-800">
                <button
                  onClick={() => void handleSignOut()}
                  disabled={isSigningOut}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-800/50 text-red-500 transition-colors text-left disabled:opacity-60"
                >
                  <i className="ri-logout-box-r-line" /> {isSigningOut ? "Signing out…" : "Sign Out"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showSessions && (
        <SessionsPanel
          onClose={() => setShowSessions(false)}
          onLoggedOut={() => {
            setShowSessions(false);
            window.history.replaceState(null, "", pageToPath("dashboard"));
          }}
        />
      )}
    </header>
    </>
  );
});

export default HRMHeader;
